import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { expressURL, authFetch } from '../../utils/networkUtils';
import { readFetchError } from '../../utils/fetchError.ts';
import { safeJoinNextPath } from '../../utils/joinPath.ts';

type InvitePreview = {
  threadName: string;
  roomType?: string;
  encryptionEnabled?: boolean;
  expiresAt?: string;
};

function Join() {
  const { token } = useParams();
  const navigate = useNavigate();
  const joinPath = safeJoinNextPath(token ? `/join/${token}` : null);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);

  useEffect(() => {
    if (!joinPath) {
      setError('This invite link is not valid.');
      return;
    }
    const tokenPart = joinPath.slice('/join/'.length);
    setError('');
    fetch(`${expressURL()}/api/join/${encodeURIComponent(tokenPart)}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(await readFetchError(response));
        }
        return response.json();
      })
      .then((data) => {
        setPreview(data);
      })
      .catch((err) => {
        setPreview(null);
        setError(err instanceof Error ? err.message : 'Invite not found');
      });
  }, [joinPath]);

  useEffect(() => {
    if (!joinPath || !preview) return undefined;
    const tokenPart = joinPath.slice('/join/'.length);
    let cancelled = false;
    setBusy(true);
    setNeedsAuth(false);
    authFetch(`${expressURL()}/api/join/${encodeURIComponent(tokenPart)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(async (response) => {
        if (cancelled) return;
        if (response.status === 401) {
          setNeedsAuth(true);
          setBusy(false);
          return;
        }
        if (!response.ok) {
          throw new Error(await readFetchError(response));
        }
        const body = await response.json();
        if (body?.threadId) {
          navigate(`/m/${body.threadId}`);
          return;
        }
        navigate('/');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not join');
        setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [joinPath, preview, navigate]);

  const nextQuery = joinPath ? `?next=${encodeURIComponent(joinPath)}` : '';

  return (
    <div className="main-content">
      <Container className="mb-5 mt-3" style={{ maxWidth: 560 }}>
        <h1 className="display-5 mb-3">Join conversation</h1>
        {preview && (
          <>
            <p>
              You were invited to <strong>{preview.threadName}</strong>
              {preview.encryptionEnabled ? ', an encrypted chat' : ''}.
            </p>
            {preview.encryptionEnabled && (
              <p className="text-muted">
                After you join, someone already in the chat must unlock it once so this device can
                read the history. The server does not hold the thread key.
              </p>
            )}
          </>
        )}
        {error && <p className="text-danger">{error}</p>}
        {preview && busy && !needsAuth && !error && <p>Joining…</p>}
        {preview && needsAuth && (
          <p>
            <Link to={`/signup${nextQuery}`}>Create an account</Link>
            {' · '}
            <Link to={`/login${nextQuery}`}>Log in</Link>
            {' to join. The confirmation email signs you in and opens the chat.'}
          </p>
        )}
      </Container>
    </div>
  );
}

export default Join;
